import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { saveDraft } from "../lib/offline";
import { useOnline } from "../lib/useOffline";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  COLLECTION_STATUSES, COLLECTION_PURPOSES, SEED_UNITS, VISIBILITY_LEVELS,
  PERMISSION_STATUSES, RATING_5, RISK_LEVELS, EXPOSURE_LEVELS, ASPECTS, LANDFORMS,
  TISSUE_TYPES, GENETIC_STATUSES,
} from "../lib/constants";
import { Sprout, MapPinned, Dna, Tent, Sun, Recycle, ChevronLeft, ChevronRight, Save, ShieldAlert } from "lucide-react";

const STEPS = [
  { key: "overview", title: "Collection overview", icon: Sprout, hint: "Who, what, when and where this seed was gathered." },
  { key: "ecological", title: "Ecological layer", icon: MapPinned, hint: "Country, habitat and the conditions that shaped this seed." },
  { key: "genetic", title: "Genetic layer", icon: Dna, hint: "Sampling, population health and genetic provenance." },
  { key: "cultural", title: "Cultural layer", icon: Tent, hint: "Traditional Owner authority, permissions and knowledge sharing." },
  { key: "climate", title: "Climate layer", icon: Sun, hint: "Current exposure and future climate suitability." },
  { key: "restoration", title: "Restoration use", icon: Recycle, hint: "How, where and with whom this seed may be used." },
];

const empty = {
  collection_id: "",
  date_collected: new Date().toISOString().slice(0, 10),
  collector_name: "",
  organisation: "",
  project_name: "",
  species_scientific_name: "",
  species_common_name: "",
  plant_family: "",
  collection_purpose: "",
  seed_quantity: "",
  seed_unit: "grams",
  collection_status: "draft",
  storage_location: "",
  visibility_level: "internal",
  ecological: {
    latitude: "", longitude: "", elevation: "", soil_type: "", soil_notes: "",
    rainfall_zone: "", recent_rainfall_notes: "", vegetation_community: "",
    associated_species: "", landform: "", slope: "", aspect: "",
    fire_history: "", disturbance_notes: "", pest_disease_notes: "",
    habitat_condition_rating: "", ecological_notes: "",
  },
  genetic: {
    dna_sample_taken: false, dna_sample_id: "", tissue_sample_type: "",
    parent_plants_sampled: "", parent_plant_spacing: "",
    population_size_estimate: "", population_health_rating: "",
    flowering_variation: "", fruiting_variation: "",
    genetic_diversity_notes: "", provenance_zone: "",
    genetic_testing_lab: "", genetic_testing_status: "",
    genetic_diversity_score: "", inbreeding_risk: "",
    hybridisation_risk: "", genetic_notes: "",
  },
  cultural: {
    traditional_owner_group: "", cultural_authority_contact: "",
    cultural_permission_status: "pending", permission_date: "",
    cultural_sensitivity_rating: "", traditional_uses: "",
    cultural_name: "", elder_observation_notes: "",
    story_permissions: "",
    public_sharing_allowed: false, partner_sharing_allowed: false, ai_analysis_allowed: false,
    restricted_knowledge_notes: "", cultural_governance_notes: "",
  },
  climate: {
    current_climate_zone: "", mean_annual_rainfall: "", mean_annual_temperature: "",
    drought_exposure: "", flood_exposure: "", cyclone_exposure: "", fire_exposure: "",
    future_climate_suitability_rating: "", climate_projection_year: 2050,
    future_rainfall_projection: "", future_temperature_projection: "",
    climate_risk_score: "", assisted_migration_suitability: "",
    climate_resilience_notes: "", recommended_future_collection_zones: "",
  },
  restoration: {
    recommended_restoration_use: "", suitable_project_types: "",
    carbon_project_suitability: "", mine_rehabilitation_suitability: "",
    biodiversity_repair_suitability: "", nursery_propagation_notes: "",
    seed_treatment_notes: "", germination_notes: "", storage_requirements: "",
    viability_testing_date: "", viability_percentage: "",
    recommended_planting_zones: "", restrictions_on_use: "",
    commercial_use_permission: "", benefit_sharing_requirements: "",
  },
};

function L({ children }) {
  return <Label className="text-[11px] tracking-[0.16em] uppercase font-semibold text-[#5C5854]">{children}</Label>;
}

function Field({ label, children, full, hint, testid }) {
  return (
    <div className={`${full ? "md:col-span-2" : ""} space-y-2`} data-testid={testid}>
      <L>{label}</L>
      {children}
      {hint && <div className="text-[11px] text-[#8A857D]">{hint}</div>}
    </div>
  );
}

function TextField({ value, onChange, type = "text", placeholder, mono = false, testid }) {
  return (
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`bg-white border-[#E6E2D8] focus-visible:ring-[#8B5A2B] ${mono ? "font-mono-data" : ""}`}
      data-testid={testid}
    />
  );
}

function NumberField({ value, onChange, step = "any", placeholder, testid }) {
  return (
    <Input
      type="number"
      step={step}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      placeholder={placeholder}
      className="bg-white border-[#E6E2D8] focus-visible:ring-[#8B5A2B] font-mono-data"
      data-testid={testid}
    />
  );
}

function TextArea({ value, onChange, rows = 3, placeholder, testid }) {
  return (
    <Textarea
      rows={rows}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-white border-[#E6E2D8] focus-visible:ring-[#8B5A2B]"
      data-testid={testid}
    />
  );
}

function Pick({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="bg-white border-[#E6E2D8]" data-testid={testid}>
        <SelectValue placeholder={placeholder || "Select"} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          const l = typeof o === "string" ? o.replace(/_/g, " ") : o.label;
          return (
            <SelectItem key={v} value={v}>{l}</SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

export default function NewCollection() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const online = useOnline();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    ...empty,
    collector_name: user?.name || "",
    organisation: user?.organisation || "",
    cultural: { ...empty.cultural, traditional_owner_group: user?.traditional_owner_group || "" },
  });

  const setRoot = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setNested = (section, k, v) => setData((d) => ({ ...d, [section]: { ...d[section], [k]: v } }));

  const buildPayload = () => {
    const payload = JSON.parse(JSON.stringify(data));
    const cleanNumeric = (obj, keys) => keys.forEach((k) => { if (obj[k] === "" || obj[k] === undefined) obj[k] = null; });
    cleanNumeric(payload, ["seed_quantity"]);
    cleanNumeric(payload.ecological, ["latitude", "longitude", "elevation"]);
    cleanNumeric(payload.genetic, ["parent_plants_sampled", "population_size_estimate", "genetic_diversity_score"]);
    cleanNumeric(payload.climate, ["mean_annual_rainfall", "mean_annual_temperature", "climate_projection_year", "climate_risk_score"]);
    cleanNumeric(payload.restoration, ["viability_percentage"]);
    return payload;
  };

  const submit = async () => {
    setSaving(true);
    const payload = buildPayload();
    try {
      if (!online) throw new Error("offline");
      const { data: res } = await api.post("/collections", payload);
      toast.success("Collection saved.");
      navigate(`/collections/${res.id}`);
    } catch (e) {
      // If we're offline OR the network call failed, save locally as a draft.
      const isNetworkError = !online || e.message === "offline" || e.code === "ERR_NETWORK" || !e.response;
      if (isNetworkError) {
        try {
          await saveDraft(payload);
          toast.success("Saved offline. It will sync automatically when you reconnect.");
          navigate("/drafts");
        } catch (saveErr) {
          toast.error("Could not save offline draft.");
        }
      } else {
        toast.error(e.response?.data?.detail || "Could not save collection.");
      }
    } finally {
      setSaving(false);
    }
  };

  const saveOfflineExplicit = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      await saveDraft(payload);
      toast.success("Saved as an offline draft on this device.");
      navigate("/drafts");
    } catch (_e) {
      toast.error("Could not save offline draft.");
    } finally {
      setSaving(false);
    }
  };

  const Step = STEPS[step].icon;

  return (
    <div className="space-y-8" data-testid="new-collection-page">
      <header>
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#8B5A2B]">New collection</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#2C2A28] mt-2">Record a seed collection</h1>
        <p className="text-[13.5px] text-[#5C5854] mt-2 max-w-2xl">
          Capture provenance across six layers. Cultural authority sits at the heart of this work — please record
          permissions carefully.
        </p>
        {!online && (
          <div className="mt-4 rounded-md border border-[#D08C38] bg-[#FFF7E8] px-4 py-3 flex items-start gap-2 text-[12.5px] text-[#9B6716]" data-testid="offline-banner">
            <span className="font-semibold tracking-[0.18em] uppercase text-[10px] mt-0.5">Field mode</span>
            <span>You&rsquo;re offline. This record will be saved on your device and synced automatically when you reconnect.</span>
          </div>
        )}
      </header>

      <ol className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="form-stepper">
        {STEPS.map((s, i) => (
          <li key={s.key}>
            <button
              type="button"
              onClick={() => setStep(i)}
              className={`w-full text-left rounded-md border px-3 py-3 transition-all ${
                i === step
                  ? "border-[#8B5A2B] bg-white shadow-sm"
                  : i < step
                  ? "border-[#E6E2D8] bg-[#F4F1EA] text-[#5C5854]"
                  : "border-dashed border-[#E6E2D8] bg-transparent text-[#8A857D]"
              }`}
              data-testid={`step-${s.key}`}
            >
              <div className="flex items-center gap-2">
                <s.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span className="text-[10px] tracking-[0.18em] uppercase font-semibold">Step {i + 1}</span>
              </div>
              <div className="text-[12.5px] font-medium mt-1 leading-tight">{s.title}</div>
            </button>
          </li>
        ))}
      </ol>

      <Card className="p-7 lg:p-9 border-[#E6E2D8] shadow-sm bg-white sk-fade-in">
        <div className="flex items-center gap-3 mb-7">
          <div className="w-9 h-9 rounded-md bg-[#F4F1EA] border border-[#E6E2D8] flex items-center justify-center">
            <Step className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.6} />
          </div>
          <div>
            <h2 className="font-display text-xl font-semibold tracking-tight text-[#2C2A28]">{STEPS[step].title}</h2>
            <p className="text-[12.5px] text-[#5C5854]">{STEPS[step].hint}</p>
          </div>
        </div>

        {/* ---------- Step content ---------- */}
        {step === 0 && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Collection ID (auto if blank)" hint="Optional · auto-generated if left blank">
              <TextField value={data.collection_id} onChange={(v) => setRoot("collection_id", v)} mono testid="f-collection-id" />
            </Field>
            <Field label="Date collected">
              <TextField type="date" value={data.date_collected} onChange={(v) => setRoot("date_collected", v)} testid="f-date" />
            </Field>
            <Field label="Collector name">
              <TextField value={data.collector_name} onChange={(v) => setRoot("collector_name", v)} testid="f-collector" />
            </Field>
            <Field label="Organisation">
              <TextField value={data.organisation} onChange={(v) => setRoot("organisation", v)} testid="f-org" />
            </Field>
            <Field label="Project name">
              <TextField value={data.project_name} onChange={(v) => setRoot("project_name", v)} testid="f-project" />
            </Field>
            <Field label="Collection purpose">
              <Pick value={data.collection_purpose} onChange={(v) => setRoot("collection_purpose", v)} options={COLLECTION_PURPOSES} testid="f-purpose" />
            </Field>
            <Field label="Species — scientific name">
              <TextField value={data.species_scientific_name} onChange={(v) => setRoot("species_scientific_name", v)} placeholder="Eucalyptus camaldulensis" testid="f-species-sci" />
            </Field>
            <Field label="Species — common name">
              <TextField value={data.species_common_name} onChange={(v) => setRoot("species_common_name", v)} placeholder="River Red Gum" testid="f-species-common" />
            </Field>
            <Field label="Plant family">
              <TextField value={data.plant_family} onChange={(v) => setRoot("plant_family", v)} placeholder="Myrtaceae" testid="f-family" />
            </Field>
            <Field label="Storage location">
              <TextField value={data.storage_location} onChange={(v) => setRoot("storage_location", v)} placeholder="Seed bank, room/shelf" testid="f-storage" />
            </Field>
            <Field label="Seed quantity">
              <NumberField value={data.seed_quantity} onChange={(v) => setRoot("seed_quantity", v)} testid="f-qty" />
            </Field>
            <Field label="Unit">
              <Pick value={data.seed_unit} onChange={(v) => setRoot("seed_unit", v)} options={SEED_UNITS} testid="f-unit" />
            </Field>
            <Field label="Collection status">
              <Pick value={data.collection_status} onChange={(v) => setRoot("collection_status", v)} options={COLLECTION_STATUSES} testid="f-status" />
            </Field>
            <Field label="Visibility level" hint="Controls who can see this record across the platform.">
              <Pick value={data.visibility_level} onChange={(v) => setRoot("visibility_level", v)} options={VISIBILITY_LEVELS} testid="f-visibility" />
            </Field>
          </div>
        )}

        {step === 1 && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="GPS latitude">
              <NumberField value={data.ecological.latitude} onChange={(v) => setNested("ecological", "latitude", v)} testid="f-lat" />
            </Field>
            <Field label="GPS longitude">
              <NumberField value={data.ecological.longitude} onChange={(v) => setNested("ecological", "longitude", v)} testid="f-lng" />
            </Field>
            <Field label="Elevation (m)">
              <NumberField value={data.ecological.elevation} onChange={(v) => setNested("ecological", "elevation", v)} testid="f-elev" />
            </Field>
            <Field label="Soil type">
              <TextField value={data.ecological.soil_type} onChange={(v) => setNested("ecological", "soil_type", v)} testid="f-soil-type" />
            </Field>
            <Field label="Soil notes" full>
              <TextArea value={data.ecological.soil_notes} onChange={(v) => setNested("ecological", "soil_notes", v)} testid="f-soil-notes" />
            </Field>
            <Field label="Rainfall zone">
              <TextField value={data.ecological.rainfall_zone} onChange={(v) => setNested("ecological", "rainfall_zone", v)} placeholder="e.g. 600–800mm" testid="f-rain-zone" />
            </Field>
            <Field label="Recent rainfall notes">
              <TextField value={data.ecological.recent_rainfall_notes} onChange={(v) => setNested("ecological", "recent_rainfall_notes", v)} testid="f-recent-rain" />
            </Field>
            <Field label="Vegetation community">
              <TextField value={data.ecological.vegetation_community} onChange={(v) => setNested("ecological", "vegetation_community", v)} testid="f-veg" />
            </Field>
            <Field label="Associated species">
              <TextField value={data.ecological.associated_species} onChange={(v) => setNested("ecological", "associated_species", v)} testid="f-assoc" />
            </Field>
            <Field label="Landform">
              <Pick value={data.ecological.landform} onChange={(v) => setNested("ecological", "landform", v)} options={LANDFORMS} testid="f-landform" />
            </Field>
            <Field label="Slope">
              <TextField value={data.ecological.slope} onChange={(v) => setNested("ecological", "slope", v)} placeholder="e.g. 12°" testid="f-slope" />
            </Field>
            <Field label="Aspect">
              <Pick value={data.ecological.aspect} onChange={(v) => setNested("ecological", "aspect", v)} options={ASPECTS} testid="f-aspect" />
            </Field>
            <Field label="Fire history">
              <TextField value={data.ecological.fire_history} onChange={(v) => setNested("ecological", "fire_history", v)} testid="f-fire" />
            </Field>
            <Field label="Habitat condition rating">
              <Pick value={data.ecological.habitat_condition_rating} onChange={(v) => setNested("ecological", "habitat_condition_rating", v)} options={RATING_5} testid="f-habitat" />
            </Field>
            <Field label="Disturbance notes" full>
              <TextArea value={data.ecological.disturbance_notes} onChange={(v) => setNested("ecological", "disturbance_notes", v)} testid="f-disturb" />
            </Field>
            <Field label="Pest or disease observations" full>
              <TextArea value={data.ecological.pest_disease_notes} onChange={(v) => setNested("ecological", "pest_disease_notes", v)} testid="f-pest" />
            </Field>
            <Field label="Ecological notes" full>
              <TextArea value={data.ecological.ecological_notes} onChange={(v) => setNested("ecological", "ecological_notes", v)} testid="f-eco-notes" />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="DNA sample taken">
              <label className="flex items-center gap-2 mt-1">
                <Checkbox checked={!!data.genetic.dna_sample_taken} onCheckedChange={(v) => setNested("genetic", "dna_sample_taken", !!v)} data-testid="f-dna-taken" />
                <span className="text-[13px] text-[#2C2A28]">Yes, a sample was taken</span>
              </label>
            </Field>
            <Field label="DNA sample ID">
              <TextField value={data.genetic.dna_sample_id} onChange={(v) => setNested("genetic", "dna_sample_id", v)} mono testid="f-dna-id" />
            </Field>
            <Field label="Tissue sample type">
              <Pick value={data.genetic.tissue_sample_type} onChange={(v) => setNested("genetic", "tissue_sample_type", v)} options={TISSUE_TYPES} testid="f-tissue" />
            </Field>
            <Field label="Number of parent plants sampled">
              <NumberField value={data.genetic.parent_plants_sampled} onChange={(v) => setNested("genetic", "parent_plants_sampled", v)} step="1" testid="f-parents" />
            </Field>
            <Field label="Distance between parent plants">
              <TextField value={data.genetic.parent_plant_spacing} onChange={(v) => setNested("genetic", "parent_plant_spacing", v)} placeholder="e.g. 50m average" testid="f-spacing" />
            </Field>
            <Field label="Population size estimate">
              <NumberField value={data.genetic.population_size_estimate} onChange={(v) => setNested("genetic", "population_size_estimate", v)} step="1" testid="f-pop-size" />
            </Field>
            <Field label="Population health rating">
              <Pick value={data.genetic.population_health_rating} onChange={(v) => setNested("genetic", "population_health_rating", v)} options={RATING_5} testid="f-pop-health" />
            </Field>
            <Field label="Provenance zone">
              <TextField value={data.genetic.provenance_zone} onChange={(v) => setNested("genetic", "provenance_zone", v)} testid="f-prov-zone" />
            </Field>
            <Field label="Flowering variation observed">
              <TextField value={data.genetic.flowering_variation} onChange={(v) => setNested("genetic", "flowering_variation", v)} testid="f-flowering" />
            </Field>
            <Field label="Fruiting variation observed">
              <TextField value={data.genetic.fruiting_variation} onChange={(v) => setNested("genetic", "fruiting_variation", v)} testid="f-fruiting" />
            </Field>
            <Field label="Genetic testing lab">
              <TextField value={data.genetic.genetic_testing_lab} onChange={(v) => setNested("genetic", "genetic_testing_lab", v)} testid="f-lab" />
            </Field>
            <Field label="Genetic testing status">
              <Pick value={data.genetic.genetic_testing_status} onChange={(v) => setNested("genetic", "genetic_testing_status", v)} options={GENETIC_STATUSES} testid="f-gen-status" />
            </Field>
            <Field label="Genetic diversity score (0–1)">
              <NumberField value={data.genetic.genetic_diversity_score} onChange={(v) => setNested("genetic", "genetic_diversity_score", v)} testid="f-diversity" />
            </Field>
            <Field label="Inbreeding risk">
              <Pick value={data.genetic.inbreeding_risk} onChange={(v) => setNested("genetic", "inbreeding_risk", v)} options={RISK_LEVELS} testid="f-inbreed" />
            </Field>
            <Field label="Hybridisation risk">
              <Pick value={data.genetic.hybridisation_risk} onChange={(v) => setNested("genetic", "hybridisation_risk", v)} options={RISK_LEVELS} testid="f-hybrid" />
            </Field>
            <Field label="Genetic diversity notes" full>
              <TextArea value={data.genetic.genetic_diversity_notes} onChange={(v) => setNested("genetic", "genetic_diversity_notes", v)} testid="f-diversity-notes" />
            </Field>
            <Field label="Genetic notes" full>
              <TextArea value={data.genetic.genetic_notes} onChange={(v) => setNested("genetic", "genetic_notes", v)} testid="f-gen-notes" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <>
            <div className="mb-6 sk-restricted-card rounded-md px-4 py-3 flex items-start gap-2 text-[12.5px] text-[#B45B3E]">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} />
              <span><strong>Cultural authority sits with Traditional Owners.</strong> Record permissions exactly as granted. Restricted fields will be hidden from users without authority.</span>
            </div>
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
              <Field label="Traditional Owner group">
                <TextField value={data.cultural.traditional_owner_group} onChange={(v) => setNested("cultural", "traditional_owner_group", v)} testid="f-to-group" />
              </Field>
              <Field label="Cultural authority contact">
                <TextField value={data.cultural.cultural_authority_contact} onChange={(v) => setNested("cultural", "cultural_authority_contact", v)} testid="f-authority" />
              </Field>
              <Field label="Cultural permission status">
                <Pick value={data.cultural.cultural_permission_status} onChange={(v) => setNested("cultural", "cultural_permission_status", v)} options={PERMISSION_STATUSES} testid="f-perm-status" />
              </Field>
              <Field label="Permission date">
                <TextField type="date" value={data.cultural.permission_date} onChange={(v) => setNested("cultural", "permission_date", v)} testid="f-perm-date" />
              </Field>
              <Field label="Cultural sensitivity rating">
                <Pick value={data.cultural.cultural_sensitivity_rating} onChange={(v) => setNested("cultural", "cultural_sensitivity_rating", v)} options={["low", "moderate", "high", "very_high"]} testid="f-sensitivity" />
              </Field>
              <Field label="Story permissions">
                <TextField value={data.cultural.story_permissions} onChange={(v) => setNested("cultural", "story_permissions", v)} placeholder="Notes on what may be shared" testid="f-story-perm" />
              </Field>
              <Field label="Cultural name (restricted)">
                <TextField value={data.cultural.cultural_name} onChange={(v) => setNested("cultural", "cultural_name", v)} testid="f-cult-name" />
              </Field>
              <Field label="Traditional uses (restricted)">
                <TextField value={data.cultural.traditional_uses} onChange={(v) => setNested("cultural", "traditional_uses", v)} testid="f-trad-uses" />
              </Field>
              <Field label="Elder observation notes (restricted)" full>
                <TextArea value={data.cultural.elder_observation_notes} onChange={(v) => setNested("cultural", "elder_observation_notes", v)} testid="f-elder" />
              </Field>
              <Field label="Restricted knowledge notes (restricted)" full>
                <TextArea value={data.cultural.restricted_knowledge_notes} onChange={(v) => setNested("cultural", "restricted_knowledge_notes", v)} testid="f-restricted" />
              </Field>
              <Field label="Cultural governance notes (restricted)" full>
                <TextArea value={data.cultural.cultural_governance_notes} onChange={(v) => setNested("cultural", "cultural_governance_notes", v)} testid="f-governance" />
              </Field>

              <Field label="Sharing permissions" full>
                <div className="grid sm:grid-cols-3 gap-3 mt-1">
                  {[
                    ["public_sharing_allowed", "Public knowledge sharing"],
                    ["partner_sharing_allowed", "Sharing with project partners"],
                    ["ai_analysis_allowed", "Use in AI analysis"],
                  ].map(([k, label]) => (
                    <label key={k} className="flex items-start gap-2 p-3 border border-[#E6E2D8] rounded-md bg-white">
                      <Checkbox checked={!!data.cultural[k]} onCheckedChange={(v) => setNested("cultural", k, !!v)} data-testid={`f-${k.replace(/_/g, '-')}`} />
                      <span className="text-[12.5px] text-[#2C2A28] leading-tight">{label}</span>
                    </label>
                  ))}
                </div>
              </Field>
            </div>
          </>
        )}

        {step === 4 && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Current climate zone">
              <TextField value={data.climate.current_climate_zone} onChange={(v) => setNested("climate", "current_climate_zone", v)} placeholder="e.g. Tropical savanna" testid="f-climate-zone" />
            </Field>
            <Field label="Mean annual rainfall (mm)">
              <NumberField value={data.climate.mean_annual_rainfall} onChange={(v) => setNested("climate", "mean_annual_rainfall", v)} testid="f-mar" />
            </Field>
            <Field label="Mean annual temperature (°C)">
              <NumberField value={data.climate.mean_annual_temperature} onChange={(v) => setNested("climate", "mean_annual_temperature", v)} testid="f-mat" />
            </Field>
            <Field label="Drought exposure">
              <Pick value={data.climate.drought_exposure} onChange={(v) => setNested("climate", "drought_exposure", v)} options={EXPOSURE_LEVELS} testid="f-drought" />
            </Field>
            <Field label="Flood exposure">
              <Pick value={data.climate.flood_exposure} onChange={(v) => setNested("climate", "flood_exposure", v)} options={EXPOSURE_LEVELS} testid="f-flood" />
            </Field>
            <Field label="Cyclone exposure">
              <Pick value={data.climate.cyclone_exposure} onChange={(v) => setNested("climate", "cyclone_exposure", v)} options={EXPOSURE_LEVELS} testid="f-cyclone" />
            </Field>
            <Field label="Fire exposure">
              <Pick value={data.climate.fire_exposure} onChange={(v) => setNested("climate", "fire_exposure", v)} options={EXPOSURE_LEVELS} testid="f-fire-exp" />
            </Field>
            <Field label="Future climate suitability rating">
              <Pick value={data.climate.future_climate_suitability_rating} onChange={(v) => setNested("climate", "future_climate_suitability_rating", v)} options={RATING_5} testid="f-suit" />
            </Field>
            <Field label="Climate projection year">
              <NumberField value={data.climate.climate_projection_year} onChange={(v) => setNested("climate", "climate_projection_year", v)} step="1" testid="f-proj-year" />
            </Field>
            <Field label="Climate risk score (0–10)">
              <NumberField value={data.climate.climate_risk_score} onChange={(v) => setNested("climate", "climate_risk_score", v)} testid="f-risk-score" />
            </Field>
            <Field label="Future rainfall projection">
              <TextField value={data.climate.future_rainfall_projection} onChange={(v) => setNested("climate", "future_rainfall_projection", v)} placeholder="e.g. −10% by 2050" testid="f-future-rain" />
            </Field>
            <Field label="Future temperature projection">
              <TextField value={data.climate.future_temperature_projection} onChange={(v) => setNested("climate", "future_temperature_projection", v)} placeholder="e.g. +1.8°C by 2050" testid="f-future-temp" />
            </Field>
            <Field label="Assisted migration suitability">
              <Pick value={data.climate.assisted_migration_suitability} onChange={(v) => setNested("climate", "assisted_migration_suitability", v)} options={RATING_5} testid="f-migration" />
            </Field>
            <Field label="Recommended future collection zones" full>
              <TextArea value={data.climate.recommended_future_collection_zones} onChange={(v) => setNested("climate", "recommended_future_collection_zones", v)} testid="f-future-zones" />
            </Field>
            <Field label="Climate resilience notes" full>
              <TextArea value={data.climate.climate_resilience_notes} onChange={(v) => setNested("climate", "climate_resilience_notes", v)} testid="f-resilience" />
            </Field>
          </div>
        )}

        {step === 5 && (
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Recommended restoration use" full>
              <TextArea value={data.restoration.recommended_restoration_use} onChange={(v) => setNested("restoration", "recommended_restoration_use", v)} testid="f-rec-use" />
            </Field>
            <Field label="Suitable project types">
              <TextField value={data.restoration.suitable_project_types} onChange={(v) => setNested("restoration", "suitable_project_types", v)} testid="f-project-types" />
            </Field>
            <Field label="Carbon project suitability">
              <Pick value={data.restoration.carbon_project_suitability} onChange={(v) => setNested("restoration", "carbon_project_suitability", v)} options={RATING_5} testid="f-carbon" />
            </Field>
            <Field label="Mine rehabilitation suitability">
              <Pick value={data.restoration.mine_rehabilitation_suitability} onChange={(v) => setNested("restoration", "mine_rehabilitation_suitability", v)} options={RATING_5} testid="f-mine" />
            </Field>
            <Field label="Biodiversity repair suitability">
              <Pick value={data.restoration.biodiversity_repair_suitability} onChange={(v) => setNested("restoration", "biodiversity_repair_suitability", v)} options={RATING_5} testid="f-biodiv" />
            </Field>
            <Field label="Nursery propagation notes" full>
              <TextArea value={data.restoration.nursery_propagation_notes} onChange={(v) => setNested("restoration", "nursery_propagation_notes", v)} testid="f-nursery" />
            </Field>
            <Field label="Seed treatment notes" full>
              <TextArea value={data.restoration.seed_treatment_notes} onChange={(v) => setNested("restoration", "seed_treatment_notes", v)} testid="f-treatment" />
            </Field>
            <Field label="Germination notes" full>
              <TextArea value={data.restoration.germination_notes} onChange={(v) => setNested("restoration", "germination_notes", v)} testid="f-germ" />
            </Field>
            <Field label="Storage requirements">
              <TextField value={data.restoration.storage_requirements} onChange={(v) => setNested("restoration", "storage_requirements", v)} testid="f-storage-req" />
            </Field>
            <Field label="Viability testing date">
              <TextField type="date" value={data.restoration.viability_testing_date} onChange={(v) => setNested("restoration", "viability_testing_date", v)} testid="f-via-date" />
            </Field>
            <Field label="Viability percentage">
              <NumberField value={data.restoration.viability_percentage} onChange={(v) => setNested("restoration", "viability_percentage", v)} testid="f-via-pct" />
            </Field>
            <Field label="Recommended planting zones">
              <TextField value={data.restoration.recommended_planting_zones} onChange={(v) => setNested("restoration", "recommended_planting_zones", v)} testid="f-planting" />
            </Field>
            <Field label="Restrictions on use" full>
              <TextArea value={data.restoration.restrictions_on_use} onChange={(v) => setNested("restoration", "restrictions_on_use", v)} testid="f-restrict-use" />
            </Field>
            <Field label="Commercial use permission">
              <Pick value={data.restoration.commercial_use_permission} onChange={(v) => setNested("restoration", "commercial_use_permission", v)} options={["allowed", "with_approval", "not_allowed"]} testid="f-commercial" />
            </Field>
            <Field label="Benefit sharing requirements" full>
              <TextArea value={data.restoration.benefit_sharing_requirements} onChange={(v) => setNested("restoration", "benefit_sharing_requirements", v)} testid="f-benefit" />
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between mt-9 pt-6 border-t border-[#E6E2D8]">
          <Button
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="gap-2 border-[#E6E2D8] text-[#2C2A28] hover:bg-[#F4F1EA]"
            data-testid="form-prev"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-[#8A857D]">
            Step {step + 1} of {STEPS.length}
          </div>
          {step < STEPS.length - 1 ? (
            <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className="gap-2 bg-[#8B5A2B] hover:bg-[#6B4420] text-[#FDFBF7]" data-testid="form-next">
              Next <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Button
                variant="outline"
                onClick={saveOfflineExplicit}
                disabled={saving}
                className="gap-2 border-[#E6E2D8] text-[#5C5854] hover:bg-[#F4F1EA]"
                data-testid="form-save-offline"
                title="Save this record on this device only — will sync when online"
              >
                <Save className="w-4 h-4" /> Save offline
              </Button>
              <Button onClick={submit} disabled={saving} className="gap-2 bg-[#4A5D4E] hover:bg-[#36453A] text-[#FDFBF7]" data-testid="form-submit">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : online ? "Save collection" : "Save (offline)"}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
